import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPlayersForCurrentPosition,
  getMyMajorSquad,
  getStrategyCaptain,
  getCurrentMatchRunning,
} from '../../../../../../redux/selector/Team';
import { getMyCurrentTeam } from '../../../../../../redux/selector/User';
import {
  setTeamCaptain,
  teamSelectPlayerOnGameField,
  teamReplacePlayerFromTheirPosition,
  removePlayerFromStaff,
} from '../../../../../../redux/actions/Team';
import { showPopupWindow } from '../../../../../../redux/actions/Popups';
import { getPlayerInformation } from '../../../../../../redux/actions/League';
import { POPUPS } from '../../../../../../service/constants';
import {
  cannotEnterXi,
  isEligibleCaptain,
  isSentOff,
} from '../../../../../../service/matchEngine';
import { simplifiedPosition } from '../../../../../../service/data';
import { CaptainMark } from '../../../../../../components/MatchMarks';
import { fontUi } from '../../../../../../theme/typography';
import {
  floatingMenuShellCss,
  floatingMenuListCss,
  floatingMenuItemCss,
} from '../../../../../../theme/selectStyles';
import { ReactComponent as InfoIcon } from '../../../../../../assets/icons/lucide/info.svg';
import { ReactComponent as SubIcon } from '../../../../../../assets/icons/lucide/arrow-left-right.svg';
import { ReactComponent as BenchIcon } from '../../../../../../assets/icons/lucide/armchair.svg';

const Menu = styled.div`
  ${fontUi};
  ${floatingMenuShellCss};
  position: fixed;
  z-index: 10050;
  min-width: 176px;
  max-width: 240px;
  max-height: min(320px, calc(100vh - 16px));
  .list {
    ${floatingMenuListCss};
    max-height: inherit;
    overflow: auto;
    overscroll-behavior: contain;
  }
  button {
    ${floatingMenuItemCss};
    width: 100%;
    border: 0;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
  }
  button > svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.textFaint};
  }
  button > .mark {
    flex-shrink: 0;
    display: inline-flex;
  }
  button:hover:not(:disabled) > svg {
    color: ${({ theme }) => theme.text};
  }
  .copy {
    min-width: 0;
    flex: 1;
  }
  .head {
    padding: 6px 10px 4px;
    font-size: 10px;
    font-weight: 700;
    color: ${({ theme }) => theme.textFaint};
  }
  .empty {
    padding: 8px 10px;
    font-size: 11px;
    color: ${({ theme }) => theme.textFaint};
  }
  .meta {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    font-weight: 500;
    color: ${({ theme }) => theme.textFaint};
  }
`;
const slotLabel = (slot) => simplifiedPosition[slot] || slot;

const canPlaySlot = (player, slot) => {
  if (!player || !slot) return false;
  const need = slotLabel(slot);
  const fav = simplifiedPosition[player.fav_position] || player.fav_position;
  const second =
    simplifiedPosition[player.second_fav_position] ||
    player.second_fav_position;
  return fav === need || (second && second === need);
};

function clampPosition(x, y, width, height) {
  const pad = 8;
  const left = Math.min(
    Math.max(pad, x),
    Math.max(pad, window.innerWidth - width - pad)
  );
  const top = Math.min(
    Math.max(pad, y),
    Math.max(pad, window.innerHeight - height - pad)
  );
  return { left, top };
}

export default function StrategyPlayerMenu({ menu, onClose, onShowSubs }) {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const players = useSelector(getPlayersForCurrentPosition) || [];
  const squad = useSelector(getMyMajorSquad) || [];
  const captain = useSelector(getStrategyCaptain);
  const matchRunning = useSelector(getCurrentMatchRunning);
  const teamInfo = useSelector(getMyCurrentTeam);
  const userTeamId = teamInfo?._id || teamInfo?.id;

  const { player, slot, inXi, mode, x, y } = menu;
  const sentOff = isSentOff(player._id, matchRunning, userTeamId);
  const captainAllowed = !!captain?.change?.allowed;
  const canCaptain = isEligibleCaptain(player) && captainAllowed;
  const isCurrentCaptain =
    captain?.player?._id &&
    String(captain.player._id) === String(player._id);

  const subs = useMemo(() => {
    if (!slot) return [];
    const xiIds = new Set(squad.map((p) => String(p._id)));
    return players
      .filter(
        (p) =>
          String(p._id) !== String(player._id) &&
          canPlaySlot(p, slot) &&
          !cannotEnterXi(p) &&
          !isSentOff(p._id, matchRunning, userTeamId)
      )
      .sort((a, b) => {
        const aXi = xiIds.has(String(a._id)) ? 0 : 1;
        const bXi = xiIds.has(String(b._id)) ? 0 : 1;
        if (aXi !== bXi) return aXi - bXi;
        return `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        );
      });
  }, [players, squad, slot, player._id, matchRunning, userTeamId]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();
    const { left, top } = clampPosition(x, y, width, height);
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
  }, [x, y, mode, subs.length]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    const onScroll = () => onClose();
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const openInfo = () => {
    dispatch(
      showPopupWindow(
        POPUPS.PLAYER_INFO_POPUP_VIEW,
        POPUPS.PLAYER_INFO_POPUP_VIEW,
        true
      )
    );
    dispatch(getPlayerInformation(player._id, POPUPS.PLAYER_INFO_POPUP_VIEW));
    onClose();
  };

  const makeCaptain = async () => {
    if (!canCaptain || isCurrentCaptain) return;
    await dispatch(setTeamCaptain(player._id));
    onClose();
  };

  const benchPlayer = () => {
    if (!inXi || sentOff) return;
    dispatch(removePlayerFromStaff(player._id));
    onClose();
  };

  const pickSub = (sub) => {
    if (!slot) return;
    dispatch(
      teamSelectPlayerOnGameField({
        _id: player._id,
        current_position: slot,
      })
    );
    dispatch(teamReplacePlayerFromTheirPosition(sub));
    onClose();
  };

  const host = document.getElementById('modal') || document.body;

  return createPortal(
    <Menu
      ref={ref}
      role="menu"
      aria-label={
        mode === 'subs'
          ? `Substitutes for ${slotLabel(slot) || 'position'}`
          : `Actions for ${player.first_name} ${player.last_name}`
      }
      style={{ left: x, top: y }}
    >
      <div className="list">
        {mode === 'subs' ? (
          <>
            <div className="head">
              Sub for {player.first_name} {player.last_name?.[0]}. (
              {slotLabel(slot)})
            </div>
            {subs.length ? (
              subs.map((sub) => (
                <button
                  key={sub._id}
                  type="button"
                  role="menuitem"
                  onClick={() => pickSub(sub)}
                >
                  <SubIcon />
                  <span className="copy">
                    {sub.first_name} {sub.last_name}
                    <span className="meta">
                      {slotLabel(sub.fav_position)}
                      {sub.second_fav_position
                        ? ` / ${slotLabel(sub.second_fav_position)}`
                        : ''}
                    </span>
                  </span>
                </button>
              ))
            ) : (
              <div className="empty">No suitable substitutes.</div>
            )}
            <button type="button" role="menuitem" onClick={onClose}>
              <span className="copy">Cancel</span>
            </button>
          </>
        ) : (
          <>
            <div className="head">
              {player.first_name} {player.last_name}
            </div>
            <button type="button" role="menuitem" onClick={openInfo}>
              <InfoIcon />
              <span className="copy">Info</span>
            </button>
            {inXi && slot && (
              <button
                type="button"
                role="menuitem"
                disabled={sentOff}
                title={
                  sentOff ? 'Sent-off players stay on the pitch' : undefined
                }
                onClick={onShowSubs}
              >
                <SubIcon />
                <span className="copy">Sub…</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              disabled={!canCaptain || isCurrentCaptain}
              title={
                isCurrentCaptain
                  ? 'Already captain'
                  : !captainAllowed
                    ? 'Captain changes are locked'
                    : !isEligibleCaptain(player)
                      ? 'This player cannot be captain'
                      : undefined
              }
              onClick={makeCaptain}
            >
              <span className="mark">
                <CaptainMark size={14} />
              </span>
              <span className="copy">
                {isCurrentCaptain ? 'Captain' : 'Make captain'}
              </span>
            </button>
            {inXi && (
              <button
                type="button"
                role="menuitem"
                disabled={sentOff}
                title={
                  sentOff ? 'Sent-off players stay on the pitch' : undefined
                }
                onClick={benchPlayer}
              >
                <BenchIcon />
                <span className="copy">To bench</span>
              </button>
            )}
          </>
        )}
      </div>
    </Menu>,
    host
  );
}
